import React from "react";
import { useSelector } from "react-redux";

import { PhotobookPreview } from "./photobook/photoBookPreview.jsx";
import { CanvasPrint3D } from "./canvas-print/CanvasPrint3D.jsx";
import { AcralicPrint3D } from "./acrylic-print/AcrylicPrint3D.jsx";
import { Calendar3D } from "./calendar/Calendar3D.jsx";
import WallPreview from "./WallPreview.jsx";
import { FoldingLayoutPreview } from "./folding/FoldingLayoutPreview.jsx";

import { EDITOR_TYPES, EDITOR_SUB_TYPES } from "../library/utils/constants/index.js";
import {
  getActiveEditorType,
  getCurrentActiveSize,
  getSettings,
  getZoom,
} from "../library/utils/helpers/index.js";

/**
 * Picks the right product preview for the current editor type.
 *
 * Lifted verbatim out of Header.jsx so the standalone /preview route (share links)
 * gets the same coverage the editor's own preview button has. It re-selects its
 * inputs from redux rather than taking them as props, so both call sites are just
 * `<ProductPreview show={...} handleClose={...} />`.
 *
 * The branch order is load-bearing: the foldable case is the catch-all for every
 * non-photobook type, which is why every other branch carries `!settings?.isFoldable`.
 * Reordering these changes which preview a foldable calendar gets.
 *
 * Note Calendar3D / CanvasPrint3D / AcralicPrint3D all render from
 * `state.svgData.currentPageSVG`, which only the live editor canvas produces
 * (Canvas.jsx). Anywhere outside the editor a canvas has to be mounted for them to
 * have anything to draw.
 */
export const ProductPreview = ({ show, handleClose }) => {
  const activeEditorType = useSelector(getActiveEditorType);
  const canvasSize = useSelector(getCurrentActiveSize);
  const settings = useSelector(getSettings);
  const zoomRatio = useSelector(getZoom);

  if (!show) return null;

  return (
    <>
      {activeEditorType === EDITOR_TYPES.PHOTOBOOK && (
        <PhotobookPreview show={show} handleClose={handleClose} />
      )}
      {activeEditorType === EDITOR_TYPES.CANVAS &&
        canvasSize?.depth > 0 &&
        !settings?.isFoldable && (
          <CanvasPrint3D
            show={show}
            handleClose={handleClose}
            depth={canvasSize?.depth}
          />
        )}
      {activeEditorType === EDITOR_TYPES.CALENDER &&
        zoomRatio &&
        settings &&
        settings.subtype !== EDITOR_SUB_TYPES.CALENDER.WALL_CALENDER &&
        !settings?.isFoldable && (
          <Calendar3D
            show={show}
            handleClose={handleClose}
            cal_settings={settings}
            zoomRatio={zoomRatio}
          />
        )}
      {activeEditorType === EDITOR_TYPES.CALENDER &&
        zoomRatio &&
        settings &&
        settings.subtype === EDITOR_SUB_TYPES.CALENDER.WALL_CALENDER &&
        !settings?.isFoldable && (
          <WallPreview show={show} handleClose={handleClose} />
        )}
      {activeEditorType === EDITOR_TYPES.ACRYLIC &&
        settings?.subtype === EDITOR_SUB_TYPES.ACRYLIC.WALL &&
        !settings?.isFoldable && (
          <WallPreview show={show} handleClose={handleClose} />
        )}
      {activeEditorType === EDITOR_TYPES.ACRYLIC &&
        settings?.subtype === EDITOR_SUB_TYPES.ACRYLIC.TABLE &&
        !settings?.isFoldable && (
          <AcralicPrint3D
            show={show}
            handleClose={handleClose}
            subtype={settings.subtype}
          />
        )}
      {settings?.isFoldable === true &&
        activeEditorType !== EDITOR_TYPES.PHOTOBOOK && (
          <FoldingLayoutPreview show={show} handleClose={handleClose} />
        )}
    </>
  );
};

export default ProductPreview;
